import {defaultSerializeQueryArgs, type SerializeQueryArgs} from '@reduxjs/toolkit/query';

import {dissoc, isEmpty} from 'ramda';

import {isWarehouseAclScoped} from './isWarehouseAclScoped';

type AclScopedArgs = {isAclScoped?: boolean} | void;

/**
 * @about Build the cache key from the route a call resolves to, not from the raw `isAclScoped`.
 * A call that stays on today's version (the flag is off, the call did not opt in, or the endpoint
 * has no new version for it) gets exactly today's key, so it shares the cache entry with every
 * other consumer of that route.
 * @param resolvesToAclScopedRoute the same condition the endpoint's `query` uses to pick the new version
 */
export const createWarehouseAclScopedSerializer =
  <QueryArgs extends AclScopedArgs>(
    resolvesToAclScopedRoute: (queryArgs: QueryArgs) => boolean
  ): SerializeQueryArgs<QueryArgs> =>
  (params) => {
    // `typeof` also narrows away the `void` of an endpoint called without an argument
    if (typeof params.queryArgs !== 'object' || resolvesToAclScopedRoute(params.queryArgs)) {
      return defaultSerializeQueryArgs(params);
    }

    // `dissoc` cannot take a key of the generic `QueryArgs`, so widen to the shape it removes
    const aclScopedArgs: {isAclScoped?: boolean} = params.queryArgs;
    const todayArgs = dissoc('isAclScoped', aclScopedArgs);

    // `getWarehouses()` is called without an argument today
    return defaultSerializeQueryArgs({
      ...params,
      queryArgs: isEmpty(todayArgs) ? undefined : todayArgs,
    });
  };

export const serializeWarehouseAclScopedArgs =
  createWarehouseAclScopedSerializer<AclScopedArgs>(isWarehouseAclScoped);
